import { AlertTriangle, Inbox, RefreshCw, ServerCog } from "lucide-react";
import type * as React from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { isApiError } from "@/lib/api/errors";
import { errorMessage } from "@/lib/api/errors";
import { cn } from "@/lib/utils";

export function EmptyState({
  icon: Icon = Inbox,
  title,
  description,
  action,
  className,
}: {
  icon?: React.ComponentType<{ className?: string }>;
  title: string;
  description?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed px-6 py-12 text-center",
        className,
      )}
    >
      <div className="grid size-11 place-items-center rounded-full bg-muted text-muted-foreground">
        <Icon className="size-5" />
      </div>
      <div className="space-y-1">
        <p className="font-medium">{title}</p>
        {description && (
          <p className="mx-auto max-w-sm text-sm text-muted-foreground">{description}</p>
        )}
      </div>
      {action}
    </div>
  );
}

/**
 * Every response carries X-Request-Id. Surfacing it means a support report maps
 * to a log line instead of a guess.
 */
export function ErrorState({
  error,
  onRetry,
  className,
  title = "That didn't load",
}: {
  error: unknown;
  onRetry?: () => void;
  className?: string;
  title?: string;
}) {
  const apiError = isApiError(error) ? error : null;
  return (
    <Card className={cn("border-destructive/30", className)}>
      <div className="flex flex-col items-start gap-3 p-5 sm:flex-row">
        <div className="grid size-10 shrink-0 place-items-center rounded-full bg-destructive/12 text-destructive">
          <AlertTriangle className="size-5" />
        </div>
        <div className="min-w-0 flex-1 space-y-1">
          <p className="font-medium">{title}</p>
          <p className="text-sm text-muted-foreground">{errorMessage(error)}</p>
          {/* Enough to diagnose from a phone: the code to switch on, the status,
              and the id support needs to find the log line. */}
          {apiError && (
            <p className="pt-1 font-mono text-[11px] break-all text-muted-foreground/70 select-all">
              {apiError.code}
              {apiError.status ? ` · HTTP ${apiError.status}` : ""}
              {apiError.requestId ? ` · ${apiError.requestId}` : ""}
            </p>
          )}
        </div>
        {onRetry && (
          <Button variant="outline" size="sm" onClick={onRetry}>
            <RefreshCw className="size-3.5" />
            Try again
          </Button>
        )}
      </div>
    </Card>
  );
}

export function LoadingCards({ count = 3, className }: { count?: number; className?: string }) {
  return (
    <div className={cn("grid gap-4 sm:grid-cols-2 xl:grid-cols-3", className)}>
      {Array.from({ length: count }, (_, i) => (
        <Card key={i} className="p-5">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="mt-3 h-7 w-32" />
          <Skeleton className="mt-4 h-2 w-full" />
        </Card>
      ))}
    </div>
  );
}

export function LoadingRows({ count = 5, className }: { count?: number; className?: string }) {
  return (
    <div className={cn("space-y-2", className)}>
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="flex items-center gap-3 rounded-xl border p-3">
          <Skeleton className="size-9 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-3 w-1/3" />
            <Skeleton className="h-3 w-1/5" />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * `not_configured` (503) means the API is missing server-side configuration —
 * a binding or a key. There is nothing the reader can do about it, so say that
 * plainly rather than implying they got something wrong.
 */
export function NotConfiguredState({
  error,
  service,
  className,
}: {
  error: unknown;
  service: string;
  className?: string;
}) {
  const requestId = isApiError(error) ? error.requestId : null;

  return (
    <Card className={cn("border-[color-mix(in_oklab,var(--warning)_35%,transparent)]", className)}>
      <div className="flex flex-col items-start gap-3 p-5 sm:flex-row">
        <div className="grid size-10 shrink-0 place-items-center rounded-full bg-[color-mix(in_oklab,var(--warning)_16%,transparent)] text-[var(--warning)]">
          <ServerCog className="size-5" />
        </div>
        <div className="min-w-0 flex-1 space-y-1">
          <p className="font-medium">{service} isn't switched on yet</p>
          <p className="text-sm text-muted-foreground">
            The server reported that {service} is missing part of its configuration, so it can't
            answer right now. This is a setup issue on the API rather than anything to do with your
            account — nothing you enter here will help until it's connected.
          </p>
          <p className="pt-1 font-mono text-[11px] break-all text-muted-foreground/70 select-all">
            not_configured · HTTP 503{requestId ? ` · ${requestId}` : ""}
          </p>
        </div>
      </div>
    </Card>
  );
}

export function InlineSpinner({ className }: { className?: string }) {
  return (
    <span
      aria-hidden
      className={cn(
        "inline-block size-4 animate-spin rounded-full border-2 border-current border-t-transparent",
        className,
      )}
    />
  );
}
